// src/services/api.js
import config from '../config.js';

const API_BASE_URL = config.API_BASE_URL;

/**
 * Cliente básico para realizar peticiones a la API
 */
export const apiClient = {
  /**
   * Realizar una petición GET
   * @param {string} endpoint - Ruta del endpoint a consultar
   * @param {Object} options - Opciones adicionales para la petición
   * @returns {Promise<any>} - Promesa con la respuesta
   */
  async get(endpoint, options = {}) {
    const jwtToken = localStorage.getItem('token');

    // Eliminar Content-Type para peticiones blob ya que puede causar problemas
    const headers = {
      ...(options.responseType !== 'blob' ? { 'Content-Type': 'application/json' } : {}),
      ...(jwtToken ? { 'Authorization': `Bearer ${jwtToken}` } : {}),
      ...(options.headers || {})
    };

    // Extraer responseType de las opciones para usarlo más adelante
    const { responseType, ...restOptions } = options;

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'GET',
      headers,
      credentials: 'include',
      ...restOptions,
    });

    if (!response.ok) {
      // Para respuestas de error, intentamos obtener detalles del error
      // Si la respuesta no es JSON, capturamos la excepción y usamos un objeto vacío
      try {
        // Solo intentar parsear como JSON si no esperamos un blob
        if (responseType !== 'blob') {
          const errorData = await response.json();
          throw new Error(errorData.detail || `Error en petición: ${response.status}`);
        } else {
          throw new Error(`Error en petición: ${response.status}`);
        }
      } catch (_parseError) {
        console.error('API Error parsing response:', _parseError);
        throw new Error(`Error en petición: ${response.status}`);
      }
    }

    // Devolver la respuesta según el tipo especificado
    if (responseType === 'blob') {
      return response.blob();
    } else if (responseType === 'text') {
      return response.text();
    } else {
      // Por defecto, devolver como JSON
      return response.json();
    }
  },

  /**
   * Realizar una petición POST
   * @param {string} endpoint - Ruta del endpoint a consultar
   * @param {Object} data - Datos a enviar en el cuerpo de la petición
   * @param {Object} options - Opciones adicionales para la petición
   * @returns {Promise<any>} - Promesa con la respuesta
   */
  async post(endpoint, data, options = {}) {
    const jwtToken = localStorage.getItem('token');

    // Extraer responseType de las opciones
    const { responseType, ...restOptions } = options;

    const headers = {
      ...(responseType !== 'blob' ? { 'Content-Type': 'application/json' } : {}),
      ...(jwtToken ? { 'Authorization': `Bearer ${jwtToken}` } : {}),
      ...(restOptions.headers || {})
    };

    // Construir el body
    let body = undefined;
    if (data !== null && data !== undefined) {
      body = JSON.stringify(data);
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers,
      body,
      credentials: 'include',
      ...restOptions,
    });

    if (!response.ok) {
      try {
        if (responseType !== 'blob') {
          const errorData = await response.json();
          throw new Error(errorData.detail || `Error en petición: ${response.status}`);
        } else {
          throw new Error(`Error en petición: ${response.status}`);
        }
      } catch {
        throw new Error(`Error en petición: ${response.status}`);
      }
    }

    // Devolver la respuesta según el tipo especificado
    if (responseType === 'blob') {
      return response.blob();
    } else if (responseType === 'text') {
      return response.text();
    } else {
      return response.json();
    }
  },

  /**
   * Realizar una petición PUT
   * @param {string} endpoint - Ruta del endpoint a actualizar
   * @param {Object} data - Datos a enviar en el cuerpo de la petición
   * @param {Object} options - Opciones adicionales para la petición
   * @returns {Promise<any>} - Promesa con la respuesta
   */
  async put(endpoint, data, options = {}) {
    const jwtToken = localStorage.getItem('token');
    
    // Extraer responseType de las opciones
    const { responseType, ...restOptions } = options;
    
    const headers = {
      ...(responseType !== 'blob' ? { 'Content-Type': 'application/json' } : {}),
      ...(jwtToken ? { 'Authorization': `Bearer ${jwtToken}` } : {}),
      ...(restOptions.headers || {})
    };

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(data),
      credentials: 'include',
      ...restOptions,
    });

    if (!response.ok) {
      try {
        if (responseType !== 'blob') {
          const errorData = await response.json();
          throw new Error(errorData.detail || `Error en petición: ${response.status}`);
        } else {
          throw new Error(`Error en petición: ${response.status}`);
        }
      } catch {
        throw new Error(`Error en petición: ${response.status}`);
      }
    }

    // Devolver la respuesta según el tipo especificado
    if (responseType === 'blob') {
      return response.blob();
    } else if (responseType === 'text') {
      return response.text();
    } else {
      return response.json();
    }
  },

  /**
   * Realizar una petición POST para subir un archivo
   * @param {string} endpoint - Ruta del endpoint a consultar
   * @param {FormData} formData - FormData con los archivos y datos a enviar
   * @param {Object} options - Opciones adicionales para la petición
   * @returns {Promise<any>} - Promesa con la respuesta
   */
  async uploadFile(endpoint, formData, options = {}) {
    // No establecemos Content-Type aquí, ya que fetch lo establece automáticamente con el boundary correcto
    const jwtToken = localStorage.getItem('token');
    const headers = {
      ...(jwtToken ? { 'Authorization': `Bearer ${jwtToken}` } : {}),
      ...(options.headers || {})
    };

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers,
      body: formData,
      credentials: 'include',
      ...options,
    });

    if (!response.ok) {
      let errorData = null;
      try {
        errorData = await response.json();
      } catch {
        // Si no se puede parsear como JSON, errorData queda null
      }

      const error = new Error(errorData?.detail || errorData?.mensaje || `Error en petición: ${response.status}`);
      error.response = {
        status: response.status,
        data: errorData
      };
      throw error;
    }

    return response.json();
  },

  /**
   * Realizar una petición DELETE
   * @param {string} endpoint - Ruta del endpoint a eliminar
   * @param {Object} options - Opciones adicionales para la petición
   * @returns {Promise<any>} - Promesa con la respuesta
   */
  async delete(endpoint, options = {}) {
    const jwtToken = localStorage.getItem('token');
    
    // Extraer responseType de las opciones
    const { responseType, ...restOptions } = options;
    
    const headers = {
      ...(responseType !== 'blob' ? { 'Content-Type': 'application/json' } : {}),
      ...(jwtToken ? { 'Authorization': `Bearer ${jwtToken}` } : {}),
      ...(restOptions.headers || {})
    };

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'DELETE',
      headers,
      credentials: 'include',
      ...restOptions,
    });

    if (!response.ok) {
      try {
        if (responseType !== 'blob') {
          const errorData = await response.json();
          throw new Error(errorData.detail || `Error en petición: ${response.status}`);
        } else {
          throw new Error(`Error en petición: ${response.status}`);
        }
      } catch {
        throw new Error(`Error en petición: ${response.status}`);
      }
    }

    // Devolver la respuesta según el tipo especificado
    if (responseType === 'blob') {
      return response.blob();
    } else if (responseType === 'text') {
      return response.text();
    } else {
      return response.json();
    }
  },
};